<template>
  <Highlighter
    :selected="selected"
    :disabled="disabled"
    class="bg-white overflow-hidden shadow rounded-lg py-6 group relative"
    @click="onQuestionClick"
  >
    <div class="flex mb-6 justify-between px-6 h-full">
      <div class="flex flex-col items-between w-full h-full">
        <div>
          <div class="flex w-full">
            <span class="inline-flex justify-center items-center px-4 py-0.5 rounded-full text-xs font-medium bg-linear-to-r from-purple-500 to-indigo-500 text-white">
              {{ t('slides_editor.components.spark_question.round', { position: question.position }) }}
            </span>

            <button
              @click.stop="onDeleteQuestion(question)"
              class="absolute top-0 mt-3 right-0 mr-3 p-1 bg-gray-200 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 ease-in-out"
            >
              <TrashIcon class="h-6 w-6 text-red-500 stroke-[2px]" />
            </button>
          </div>

          <div class="tag text-base text-left mt-6 ml-1 min-h-[15px]">
            <div class="trix-content" v-html="question.text"></div>
          </div>
        </div>

        <div v-if="question.variant_attributes?.answers_attributes?.length" class="mt-6 w-full rounded-lg border border-gray-200 divide-y divide-gray-200 overflow-hidden">
          <div
            v-for="(answer, index) in question.variant_attributes.answers_attributes"
            :key="index"
            class="bg-white border-gray-200 relative p-4 grid grid-cols-10 cursor-pointer"
          >
            <div class="col-span-10 flex justify-start items-center text-left">
              <div
                v-html="getAnswerIcon(index)"
                class="text-gray-500 mr-3"
              />
              <span class="text-gray-900 block text-sm font-normal mr-3 break-word">
                {{ answer.text }}
              </span>

              <div class="ml-10" v-if="answer.correct">
                <div class="text-green-800 flex items-center">
                  <CheckCircleIcon class="h-6 w-6 text-green-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Highlighter>
</template>

<script setup>
import { inject, computed } from 'vue'
import Highlighter from '@/features/slides_editor/components/slides/blocks/Highlighter.vue'
import { CheckCircleIcon } from '@heroicons/vue/24/solid'
import { TrashIcon } from '@heroicons/vue/24/outline'
import { useSidePanelHelpers, useConfirmationDialog, useBlockUpdate } from '@/features/slides_editor/composables'
import { SPARK_ICONS } from '@/helpers/constants/spark_icons'
import { t } from '@/libs/i18n'

const props = defineProps({
  question: {
    type: Object,
    required: true
  },
  block: {
    type: Object,
    required: true
  },
  selected: {
    type: Boolean,
    default: false
  }
})

const { replaceInStack, pushToStack, popFromStack, activePanel } = useSidePanelHelpers()
const { onBlockUpdate } = useBlockUpdate(props.block, { showErrorStatus: false })

const emit = defineEmits(['select'])

const errors = inject('errors')

const disabled = computed(() => {
  return Object.keys(errors.value).length > 0
})

const onQuestionClick = () => {
  if (disabled.value) {
    return
  }

  emit('select', props.question.id)

  const panel = {
    content: {
      title: t('slides_editor.components.spark_question.question_header', { position: props.question.position }),
      titleClass: 'text-base text-gray-900 font-medium',
      component: 'SparkQuestionForm',
      props: { question: props.question, block: props.block }
    }
  }

  if (activePanel.value.content.component === 'SparkQuestionForm') {
    replaceInStack(panel)
  } else {
    pushToStack(panel)
  }
}

const onDeleteQuestion = async (question) => {
  const confirmation = await useConfirmationDialog({
    title: t('slides_editor.components.spark_question.confirmation.title'),
    body: t('slides_editor.components.spark_question.confirmation.body'),
    confirmBtnText: t('slides_editor.components.spark_question.confirmation.confirm'),
    cancelBtnText: t('slides_editor.components.spark_question.confirmation.cancel')
  })

  if (confirmation) {
    const spark = props.block.section.spark

    const sectionParams = {
      id: props.block.section.id,
      section_spark_attributes: {
        id: spark.section_spark_id,
        spark_attributes: {
          id: spark.id,
          questions_attributes: spark.questions.map(q => {
            if (q.id === question.id) {
              return {
                ...q,
                _destroy: 1
              }
            }

            return q
          })
        }
      }
    }

    await onBlockUpdate(sectionParams, 'section_attributes')

    if (activePanel.value.content.component === 'SparkQuestionForm' && activePanel.value.content.props.question.id === question.id) {
      popFromStack()
    }
  }
}

const getAnswerIcon = (index) => {
  switch (index) {
    case 0:
      return SPARK_ICONS.square
    case 1:
      return SPARK_ICONS.triangle
    case 2:
      return SPARK_ICONS.hexagon
    case 3:
      return SPARK_ICONS.circle
    default:
      return SPARK_ICONS.square
  }
}

</script>
